"use client";

import FileOutline from "@/assets/file-outline.svg";
import { Button } from "@/components/ui/button";
import { TinaMarkdown } from "@/lib/utils";
import { Download, Eye } from "lucide-react";
import { useEffect, useState } from "react";
import ReactPlayer from "react-player";

function Video({ url }) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!url || !isMounted) return null;

  return (
    <div className="my-6 overflow-hidden rounded-lg h-[240px] md:h-[404px]">
      <ReactPlayer url={url} className="w-full [&>iframe]:w-full" width="100%" height="100%" controls />
    </div>
  );
}

function fileNameFromUrl(url) {
  try {
    const pathname = new URL(url).pathname;
    const segment = pathname.split("/").pop() || "";
    return decodeURIComponent(segment);
  } catch {
    const segment = url.split("/").pop() || "";
    return decodeURIComponent(segment.split("?")[0]);
  }
}

function fileExtension(url) {
  const name = fileNameFromUrl(url);
  const extension = name.split(".").pop()?.toLowerCase();
  if (!extension || extension === name.toLowerCase()) return "";
  return extension;
}

function badgeClassName(extension) {
  if (extension === "pdf") return "bg-red-500";
  if (extension === "doc" || extension === "docx") return "bg-green-500";
  return "bg-primary";
}

function downloadUrl(url, filename) {
  if (!url.includes("/upload/")) return url;
  const safeName = filename.replace(/[^\w.-]+/g, "_").replace(/^_+|_+$/g, "");
  const flag = safeName ? `fl_attachment:${safeName}` : "fl_attachment";
  return url.replace("/upload/", `/upload/${flag}/`);
}

function File({ name, file }) {
  const url = typeof file === "string" ? file : "";
  const title = name?.trim() || (url ? fileNameFromUrl(url) : "");
  const extension = url ? fileExtension(url) : "";

  if (!url || !title) return null;

  return (
    <div className="not-prose my-6 flex flex-col gap-3 rounded-lg border border-border bg-white p-3 sm:flex-row sm:items-center sm:gap-4 sm:p-4">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="relative shrink-0">
          {extension && (
            <span className={`absolute bottom-2 left-[1px] rounded-[2px] px-1 text-[8px] font-bold uppercase text-white ${badgeClassName(extension)}`}>
              {extension}
            </span>
          )}
          <FileOutline className="size-10 text-muted-foreground" aria-hidden="true" />
        </div>
        <span className="min-w-0 truncate text-[15px] font-medium leading-none text-title">{title}</span>
      </div>
      <div className="flex shrink-0 items-center justify-end gap-2">
        <Button variant="outline" className="h-9 px-3 text-sm" asChild>
          <a href={url} target="_blank" rel="noopener noreferrer" className="!no-underline">
            <Eye />
            View
          </a>
        </Button>
        <Button className="h-9 px-3 text-sm !text-white" asChild variant="default">
          <a href={downloadUrl(url, title)} className="!no-underline">
            <Download />
            Download
          </a>
        </Button>
      </div>
    </div>
  );
}

const thClassName =
  "border-b border-border px-4 py-3 text-left text-[15px] font-medium text-title align-top min-w-[7.5rem] first:min-w-[9rem] sm:first:min-w-[10.5rem]";

const tdClassName =
  "border-b border-border px-4 py-3 text-left text-[15px] font-light align-top leading-relaxed min-w-[7.5rem] first:min-w-[9rem] sm:first:min-w-[10.5rem]";

function ArticleTh({ children, align }) {
  return (
    <th className={thClassName} style={{ textAlign: align || "left" }}>
      {children}
    </th>
  );
}

function ArticleTd({ children, align }) {
  return (
    <td className={tdClassName} style={{ textAlign: align || "left" }}>
      {children}
    </td>
  );
}

function ArticleTable(props) {
  const { children, tableRows, firstRowHeader, align = [] } = props;

  if (children) {
    return (
      <div className="prose-table-wrap">
        <table className="article-table">{children}</table>
      </div>
    );
  }

  if (!Array.isArray(tableRows) || tableRows.length === 0) {
    return null;
  }

  const header = firstRowHeader ? tableRows[0] : null;
  const bodyRows = firstRowHeader ? tableRows.slice(1) : tableRows;

  return (
    <div className="prose-table-wrap">
      <table className="article-table">
        {header?.tableCells?.length > 0 && (
          <thead className="bg-muted">
            <tr>
              {header.tableCells.map((cell, index) => (
                <TinaMarkdown
                  key={`th-${index}`}
                  content={cell.value}
                  components={{
                    p: (pProps) => <ArticleTh align={align[index]}>{pProps.children}</ArticleTh>,
                  }}
                />
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {bodyRows.map((row, rowIndex) => (
            <tr key={`tr-${rowIndex}`} className="hover:bg-muted/50">
              {row.tableCells?.map((cell, cellIndex) => (
                <TinaMarkdown
                  key={`td-${rowIndex}-${cellIndex}`}
                  content={cell.value}
                  components={{
                    p: (pProps) => <ArticleTd align={align[cellIndex]}>{pProps.children}</ArticleTd>,
                  }}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const markdownComponents = {
  Video,
  File,
  table: ArticleTable,
  th: ArticleTh,
  td: ArticleTd,
  tr: ({ children }) => <tr className="hover:bg-muted/50">{children}</tr>,
};

export default function ArticleRichText({ content }) {
  return (
    <div className="prose prose-lg max-w-none">
      <TinaMarkdown content={content} components={markdownComponents} />
    </div>
  );
}
